import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, ArrowRight, HelpCircle } from 'lucide-react';

const faqs = [
  {
    q: 'Does IMCAC provide training?',
    a: 'No. IMCAC does not provide training, workshops, academic programs, or educational courses. IMCAC is an independent competency assessment and certification body. CPD Recognition is an evaluation service for educational activities delivered by independent third-party organizations.',
  },
  {
    q: 'Can training providers apply?',
    a: 'Yes. Independent organizations, training providers, universities, professional societies, and other educational bodies may apply for CPD Recognition of their educational activities.',
  },
  {
    q: 'Does CPD Recognition mean accreditation?',
    a: 'No. CPD Recognition evaluates educational activities for professional development purposes and does not constitute institutional accreditation, governmental approval, university accreditation, or regulatory endorsement.',
  },
  {
    q: 'What types of educational activities can be submitted?',
    a: 'Eligible activities include online courses, conferences, congresses, workshops, symposia, fellowship programs, webinars, faculty development programs, clinical skills programs, healthcare quality programs, leadership programs, and digital health programs.',
  },
  {
    q: 'How are CPD credits calculated?',
    a: 'The general framework is 1 hour of verified learning = 1 CPD credit. However, credits are assigned following review and may vary based on educational design, assessment methodology, and the complexity of the learning activity.',
  },
  {
    q: 'What are the different recognition levels?',
    a: 'There are three levels: Level 1 (IMCAC CPD Recognized) for programs meeting educational objectives standards, Level 2 (IMCAC CPD Quality Assured) for programs demonstrating quality learner assessment and faculty oversight, and Level 3 (IMCAC Advanced CPD Recognized) for programs with strong competency-based design and measurable outcomes.',
  },
  {
    q: 'How long does the review process take?',
    a: 'The standard review process takes approximately 4–6 weeks from the date of complete application submission. Complex applications may require additional time.',
  },
  {
    q: 'How long is CPD Recognition valid?',
    a: 'CPD Recognition is typically valid for a defined period as specified in the recognition certificate. Organizations may apply for renewal before the recognition period expires.',
  },
  {
    q: 'Is CPD Recognition linked to IMCAC certification examinations?',
    a: 'CPD Recognition is separate from IMCAC certification examinations. CPD credits earned through recognized activities may support ongoing professional development and certification maintenance requirements, but recognition of educational activities does not influence examination content, scoring, or certification decisions.',
  },
  {
    q: 'Does CPD Recognition give an advantage in IMCAC examinations?',
    a: 'No. IMCAC maintains complete independence between CPD Recognition activities and examination/certification decisions. Recognition of educational activities provides no preferential treatment in any IMCAC examination.',
  },
];

export default function CPDRecognitionFAQPage() {
  return (
    <div data-testid="cpd-recognition-faq-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">CPD Recognition</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Frequently Asked Questions</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC CPD Recognition evaluates educational activities for Continuing Professional Development purposes only. Recognition does not constitute governmental approval, university accreditation, specialist recognition, professional licensure, degree granting authority, or regulatory endorsement.
            </p>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-4xl">
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card key={i} className="imcac-card" data-testid={`faq-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-base font-semibold text-[#1B2A4A] font-heading mb-2">{faq.q}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F8FAFC] py-12">
        <div className="imcac-container text-center">
          <h2 className="text-xl font-bold text-[#1B2A4A] font-heading mb-4">Have More Questions?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/cpd-recognition/apply">
              <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm px-8">
                Apply for CPD Recognition <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
